<template>
  <div class="admin-ride-accommodation-links">
    <NavBar />
    <div class="container py-5" style="max-width: 800px; padding-top: 100px !important;">
      <h2 class="mb-4">Ride Accommodation Links</h2>

      <div class="d-flex mb-4">
        <b-form-input
          v-model="rideIdInput"
          type="number"
          placeholder="Enter ride ID"
          class="mr-2"
          style="max-width: 200px;"
          @keyup.enter="loadRide"
        />
        <b-button variant="primary" :disabled="loading" @click="loadRide">
          <b-spinner v-if="loading" small class="mr-1" />
          Load
        </b-button>
      </div>

      <div v-if="error" class="text-danger mb-3">{{ error }}</div>

      <div v-if="rideName" class="mb-4">
        <h4>{{ rideName }} <span class="text-muted ride-id">(ID: {{ loadedRideId }})</span></h4>
      </div>

      <div v-if="accommodations.length === 0 && rideName" class="text-muted">
        No accommodations found for this ride.
      </div>

      <div v-for="accommodation in accommodations" :key="accommodation.id" class="mb-4">
        <b-card class="shadow-sm">
          <h5 class="mb-2">{{ accommodation.name }}</h5>

          <div class="mb-2">
            <p class="font-weight-bold mb-1">Current link:</p>
            <a
              v-if="accommodation.link"
              :href="accommodation.link"
              target="_blank"
              rel="noopener"
              class="text-break link-text"
            >{{ accommodation.link }}</a>
            <span v-else class="text-muted link-text">No link</span>
          </div>

          <div v-if="accommodation.link" class="mb-2">
            <p class="font-weight-bold mb-1">Affiliate link (preview):</p>
            <a
              :href="buildAffiliateLink(accommodation.link)"
              target="_blank"
              rel="noopener"
              class="text-break link-text text-success"
            >{{ buildAffiliateLink(accommodation.link) }}</a>
          </div>

          <div class="mb-3">
            <p class="font-weight-bold mb-1">Link to save:</p>
            <b-form-input v-model="accommodation.editableLink" />
          </div>

          <div class="d-flex flex-wrap">
            <b-button
              variant="primary"
              class="mb-2"
              :disabled="accommodation.saving"
              @click="save(accommodation)"
            >
              <b-spinner v-if="accommodation.saving" small class="mr-1" />
              Save
            </b-button>
          </div>

          <p v-if="accommodation.saveError" class="text-danger mt-1 error-text">{{ accommodation.saveError }}</p>
          <p v-if="accommodation.saved" class="text-success mt-1 error-text">Saved!</p>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import NavBar from '@/components/NavBar';
import api from '@/api/accommodation';

const AFFILIATE_PREFIX = 'https://www.tkqlhce.com/click-101453709-17293139?url=';
const INTERNAL_HOME_PATH = '/';

export default {
  name: 'AdminRideAccommodationLinks',
  components: { NavBar },
  data() {
    return {
      rideIdInput: '',
      loadedRideId: null,
      rideName: '',
      accommodations: [],
      loading: false,
      error: null,
    };
  },
  created() {
    if (this.$store.getters.userId !== 1) {
      this.$router.push(INTERNAL_HOME_PATH);
    }
  },
  methods: {
    buildAffiliateLink(link) {
      if (!link) return '';
      const cleanLink = link.split('?')[0];
      return AFFILIATE_PREFIX + encodeURIComponent(cleanLink);
    },
    async loadRide() {
      const rideId = parseInt(this.rideIdInput, 10);
      if (!rideId) {
        this.error = 'Please enter a valid ride ID.';
        return;
      }
      this.loading = true;
      this.error = null;
      this.rideName = '';
      this.accommodations = [];
      try {
        const token = this.$store.state.appauth.apptoken;
        const response = await api.fetchRideAccommodationsForAdmin(token, rideId);
        const data = response.data;
        if (data.ServerMassageCode === 'not_found') {
          this.error = `Ride ${rideId} not found.`;
          return;
        }
        if (data.ServerMassageCode !== 'success') {
          this.error = 'Failed to load ride.';
          return;
        }
        this.loadedRideId = rideId;
        this.rideName = data.ride_name;
        this.accommodations = data.accommodations.map(a => ({
          ...a,
          editableLink: a.link ? this.buildAffiliateLink(a.link) : '',
          saving: false,
          saveError: null,
          saved: false,
        }));
      } catch (e) {
        this.error = 'Network error. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    async save(accommodation) {
      accommodation.saving = true;
      accommodation.saveError = null;
      accommodation.saved = false;
      try {
        const token = this.$store.state.appauth.apptoken;
        const response = await api.updateAccommodationLink(token, accommodation.id, accommodation.editableLink);
        if (response.data.ServerMassageCode !== 'success') {
          accommodation.saveError = 'Save failed. Please try again.';
          return;
        }
        accommodation.saved = true;
      } catch (e) {
        accommodation.saveError = 'Network error. Please try again.';
      } finally {
        accommodation.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.admin-ride-accommodation-links h2 { font-size: 28px; }
.admin-ride-accommodation-links h4 { font-size: 22px; }
.admin-ride-accommodation-links h5 { font-size: 18px; }
.admin-ride-accommodation-links p,
.admin-ride-accommodation-links .error-text { font-size: 16px; }
.admin-ride-accommodation-links .link-text { font-size: 14px; }
.admin-ride-accommodation-links .ride-id { font-size: 16px; }
.admin-ride-accommodation-links .btn { font-size: 16px; }
</style>
